import React, { useEffect, useState } from "react";
import { Check, CheckCircle2, Coins, Copy, ExternalLink, KeyRound, Loader2, ShieldCheck, Sparkles, X, Zap } from "lucide-react";
import { PRODUCTS, type ProductSkuId } from "@limeshare/shared";
import { api } from "../api";
import { errorMessage } from "../util";

const STORAGE_PASS_KEY = "limeshare.pass_code.v1";

export function getSavedPassCode(): string {
  if (typeof localStorage === "undefined") return "";
  try {
    return localStorage.getItem(STORAGE_PASS_KEY) ?? "";
  } catch {
    return "";
  }
}

export function savePassCode(code: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_PASS_KEY, code.trim().toUpperCase());
  } catch {
    /* ignore */
  }
}

interface CryptoCheckoutModalProps {
  defaultSku: ProductSkuId;
  allowedSkus?: ProductSkuId[];
  transferId?: string;
  title?: string;
  subtitle?: string;
  onSuccess: (passCode: string, sku: ProductSkuId) => void;
  onClose: () => void;
}

export function CryptoCheckoutModal({
  defaultSku,
  allowedSkus,
  transferId,
  title = "Unlock Instant Zero-KYC Capacity",
  subtitle = "No account or registration required. Pay once with 100+ cryptocurrencies (USDT, BTC, SOL, TON, LTC, XMR) and receive an instant Pass Code.",
  onSuccess,
  onClose,
}: CryptoCheckoutModalProps) {
  const skuList: ProductSkuId[] = allowedSkus ?? [defaultSku, "vault-pass-15", "pro-annual", "teams-25"];
  const [selectedSku, setSelectedSku] = useState<ProductSkuId>(defaultSku);
  const [existingCode, setExistingCode] = useState(getSavedPassCode());
  const [showRedeem, setShowRedeem] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<{
    orderId: string;
    invoiceId: string;
    payUrl: string;
    amountUsd: number;
    passCode: string;
    status: "waiting" | "confirming" | "confirmed";
  } | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);

  const product = PRODUCTS[selectedSku];

  // Poll order status when waiting for crypto confirmation
  useEffect(() => {
    if (!invoice || invoice.status === "confirmed") return;
    const timer = setInterval(async () => {
      try {
        const st = await api.orderStatus(invoice.orderId);
        if (st.status === "confirmed" && st.passCode) {
          savePassCode(st.passCode);
          setInvoice((prev) => (prev ? { ...prev, status: "confirmed", passCode: st.passCode! } : prev));
          clearInterval(timer);
          onSuccess(st.passCode, selectedSku);
        }
      } catch {
        /* retry next tick */
      }
    }, 3500);
    return () => clearInterval(timer);
  }, [invoice, onSuccess, selectedSku]);

  const startCheckout = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await api.checkout({
        sku: selectedSku,
        ...(transferId ? { transferId } : {}),
        returnPath: window.location.pathname,
      });
      savePassCode(res.passCode);
      setInvoice({
        orderId: res.orderId,
        invoiceId: res.invoiceId,
        payUrl: res.payUrl,
        amountUsd: res.amountUsd,
        passCode: res.passCode,
        status: res.status,
      });
      if (res.status === "confirmed") {
        onSuccess(res.passCode, selectedSku);
      } else {
        window.open(res.payUrl, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRedeemCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = existingCode.trim().toUpperCase();
    if (!clean) return;
    setError(null);
    setBusy(true);
    try {
      const st = await api.orderStatus(clean);
      if (st.status !== "confirmed" || st.creditsRemaining <= 0) {
        throw new Error(
          st.status !== "confirmed"
            ? `Pass Code is still ${st.status} (awaiting blockchain confirmation).`
            : "This Pass Code has 0 remaining credits.",
        );
      }
      if (transferId) {
        await api.extend(transferId, { passCode: clean });
      }
      savePassCode(clean);
      onSuccess(clean, st.sku);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!invoice) return;
    await navigator.clipboard.writeText(invoice.passCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-panel" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-box">
            <Zap size={20} color="#00ff66" />
            <div className="modal-title-text">
              <span className="modal-filename">{title}</span>
              <span className="modal-filesize">NOWPayments · 100+ Coins · Zero Signup</span>
            </div>
          </div>
          <button className="btn ghost btn-icon" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ flexDirection: "column", alignItems: "stretch", padding: 22 }}>
          <p className="hint" style={{ margin: "0 0 16px" }}>{subtitle}</p>

          {!invoice ? (
            <>
              {/* Plan Selector */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
                {skuList.map((skuId) => {
                  const item = PRODUCTS[skuId];
                  if (!item) return null;
                  const active = selectedSku === skuId;
                  return (
                    <div
                      key={skuId}
                      onClick={() => setSelectedSku(skuId)}
                      style={{
                        padding: "14px 16px",
                        borderRadius: 12,
                        border: active ? "2px solid var(--lime)" : "1px solid var(--border)",
                        background: active ? "rgba(0, 255, 102, 0.08)" : "var(--panel-2)",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <b style={{ fontSize: 15, color: "var(--text)" }}>{item.name}</b>
                          {item.badge && (
                            <span className="badge" style={{ padding: "2px 8px", fontSize: 11 }}>
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <div className="hint" style={{ fontSize: 12, marginTop: 2 }}>{item.description}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 20, fontWeight: 900, color: "var(--lime)" }}>
                          ${item.priceUsd.toFixed(2)}
                        </div>
                        <small className="hint" style={{ fontSize: 11 }}>one-time crypto</small>
                      </div>
                    </div>
                  );
                })}
              </div>

              {error && <div className="alert error" style={{ marginBottom: 14 }}>{error}</div>}

              <button className="btn block" disabled={busy} onClick={startCheckout}>
                {busy ? (
                  <>
                    <Loader2 size={16} /> Creating Crypto Invoice…
                  </>
                ) : (
                  <>
                    <Coins size={16} /> Pay ${product.priceUsd.toFixed(2)} with Crypto (No Signup)
                  </>
                )}
              </button>

              <div style={{ marginTop: 16, textAlign: "center" }}>
                <button
                  type="button"
                  className="btn ghost btn-sm"
                  onClick={() => setShowRedeem(!showRedeem)}
                >
                  <KeyRound size={13} /> {showRedeem ? "Hide Pass Code input" : "Already have a Pass Code (LS-XXXX…)?"}
                </button>
              </div>

              {showRedeem && (
                <form onSubmit={handleRedeemCode} style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <input
                    className="textinput"
                    style={{ marginTop: 0, fontFamily: "ui-monospace, monospace", textTransform: "uppercase" }}
                    placeholder="LS-XXXX-XXXX-XXXX"
                    value={existingCode}
                    onChange={(e) => setExistingCode(e.target.value)}
                  />
                  <button className="btn" type="submit" disabled={busy}>
                    Apply Code
                  </button>
                </form>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ display: "inline-flex", padding: 12, borderRadius: "50%", background: "rgba(0,255,102,0.12)", color: "var(--lime)", marginBottom: 12 }}>
                {invoice.status === "confirmed" ? <CheckCircle2 size={32} /> : <Loader2 size={32} />}
              </div>
              <h3 style={{ margin: "0 0 6px" }}>
                {invoice.status === "confirmed" ? "Payment Confirmed! Pass Activated" : "Awaiting Crypto Payment Confirmation…"}
              </h3>
              <p className="hint" style={{ margin: "0 0 14px" }}>
                Save your anonymous 16-character Pass Code below. It activates automatically as soon as your transaction is detected.
              </p>

              <div className="linkbox" style={{ maxWidth: 420, margin: "0 auto 16px" }}>
                <input
                  readOnly
                  value={invoice.passCode}
                  style={{ textAlign: "center", fontWeight: 800, fontSize: 16, letterSpacing: "0.08em" }}
                />
                <button className="btn ghost" onClick={copyCode}>
                  {copiedCode ? <Check size={15} /> : <Copy size={15} />}
                </button>
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <a
                  href={invoice.payUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn"
                  style={{ textDecoration: "none" }}
                >
                  <ExternalLink size={15} /> Open Payment Page (${invoice.amountUsd.toFixed(2)})
                </a>
                <button
                  className="btn ghost"
                  onClick={async () => {
                    try {
                      const st = await api.orderStatus(invoice.orderId);
                      if (st.status === "confirmed" && st.passCode) {
                        onSuccess(st.passCode, selectedSku);
                      } else {
                        setError(`Status: ${st.status}. Complete payment in the NOWPayments tab first.`);
                      }
                    } catch (e) {
                      setError(errorMessage(e));
                    }
                  }}
                >
                  I've Paid — Check Status
                </button>
              </div>
              {error && <div className="alert warn" style={{ marginTop: 14 }}>{error}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
